import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import SweetAlert from "react-bootstrap-sweetalert"

import LoadingSpinner from "components/Common/LoadingSpinner"
import { get, post } from "helpers/api_helper"

const getTeacherData = response => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.data)) return response.data.data
  if (Array.isArray(response)) return response
  if (response?.data && typeof response.data === "object") {
    return [response.data]
  }

  return []
}

const getErrorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error?.message ||
  "Something wrong, please try again"

const AddTeacher = props => {
  const [email, setEmail] = useState("")
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([])
  const [search, setSearch] = useState("")
  const [isFetching, setIsFetching] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [warningAlert, setWarningAlert] = useState(false)
  const [successAlert, setSuccessAlert] = useState(false)
  const [errorAlert, setErrorAlert] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedTeacherData, setSelectedTeacherData] = useState([])

const searchTeachers = async () => {
    if (email.length < 5) {
        alert("Email minimal 5 karakter")
        return
    }

    try {
      setLoadingSearch(true)
      setIsFetching(true)

      const res = await get(`/user/teachers?email=${email}`)
      const data = res.data.data
      selectedTeacherData.forEach((val) => {
        const findIdx = data.findIndex(v => v.id === val.id)
        if(findIdx !== -1){
          data.splice(findIdx, 1)
        }
      })
      const combined = [...selectedTeacherData, ...data]
      setTeachers(getTeacherData(combined).filter(Boolean))

    } catch (error) {
      setTeachers([])
      setErrorMessage(getErrorMessage(error))
      setErrorAlert(true)
    } finally {
      setIsFetching(false)
      setLoadingSearch(false)
    }
  }

  useEffect(() => {
    if (props.modalShow) {
      setSelectedTeacherIds([])
      setSearch("")
    }
  }, [props.modalShow])

  const filteredTeachers = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    if (!keyword) return teachers

    return teachers.filter(teacher =>
      [teacher?.full_name, teacher?.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    )
  }, [search, teachers])

  const toggleTeacher = (teacherId, idx) => {
    setSelectedTeacherIds(current =>
      current.includes(teacherId)
        ? current.filter(id => id !== teacherId)
        : [...current, teacherId]
    )
    const combinedSelected = selectedTeacherData
    const findIdx = combinedSelected.findIndex(v => v.id === teacherId)
    if (findIdx !== -1) {
      combinedSelected.splice(findIdx,1)
    }else{
      combinedSelected.push(teachers[idx])
    }
    setSelectedTeacherData(combinedSelected)
  }

  const handleClose = () => {
    if (isSaving) return

    setSelectedTeacherIds([])
    setSelectedTeacherData([])
    setSearch("")
    props.toggleModal()
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (selectedTeacherIds.length === 0) {
      setWarningAlert(true)
      return
    }

    const numericClassId = Number(props.classId)
    const body = {
      class_id: Number.isNaN(numericClassId) ? props.classId : numericClassId,
      teacher_id: selectedTeacherIds,
      is_main: false,
      is_lock: true,
      is_temporary: false,
    }

    setIsSaving(true)

    try {
      await post("/class/add_teacher", body)
      setSuccessAlert(true)
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
      setErrorAlert(true)
    } finally {
      setIsSaving(false)
      handleClose()
    }
  }

  const handleSuccess = () => {
    setSuccessAlert(false)
    setSelectedTeacherIds([])
    setSearch("")
    props.toggleModal()
    props.onSuccess?.()
  }

  return (
    <>
      <Modal
        size="lg"
        centered={true}
        isOpen={props.modalShow}
        toggle={handleClose}
        scrollable={true}
      >
        <ModalHeader toggle={handleClose}>Add Homeroom Teacher</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <Row className="align-items-center">
              <Col md="10">
                <FormGroup>
                  <Label for="email">Email</Label>
                      <Input
                      type="text"
                      id="email"
                      placeholder="Input email minimal 5 character"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      />
                </FormGroup>
              </Col>

              <Col md="2" className="d-flex align-items-center">
                <Button
                    color="primary"
                    className="btn-sm w-100 mt-3"
                    onClick={searchTeachers}
                    disabled={loadingSearch}
                >
                    {loadingSearch ? "Searching..." : "Search"}
                </Button>
              </Col>
            </Row>

            <FormGroup>
              <Label>Select Teacher</Label>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #ced4da",
                  borderRadius: "5px",
                }}
              >
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th width="50">#</th>
                      <th>Name</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isFetching ? (
                      <tr>
                        <td colSpan="3">
                          <div className="d-flex flex-column align-items-center justify-content-center py-4">
                            <LoadingSpinner />
                            <div className="mt-2">
                              Fetching data, please wait...
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredTeachers.length > 0 ? (
                      filteredTeachers.map((teacher, idx) => {
                        const isSelected = selectedTeacherIds.includes(
                          teacher.id
                        )

                        return (
                          <tr
                            key={teacher.id}
                            onClick={() => toggleTeacher(teacher.id, idx)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isSelected
                                ? "#e3f2fd"
                                : "transparent",
                            }}
                          >
                            <td>
                              <Input
                                type="checkbox"
                                aria-label={`Select ${
                                  teacher.full_name || "teacher"
                                }`}
                                checked={isSelected}
                                onClick={event => event.stopPropagation()}
                                onChange={() => toggleTeacher(teacher.id)}
                              />
                            </td>
                            <td>{teacher.full_name || "-"}</td>
                            <td>{teacher.email || "-"}</td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-3">
                          No Data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </FormGroup>

            <Row>
              <Col md="12">
                <div className="d-flex flex-row-reverse">
                  <Button
                    className="btn-sm"
                    color="danger"
                    type="button"
                    onClick={handleClose}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="btn-sm mx-1"
                    color="primary"
                    type="submit"
                    disabled={isFetching || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Saving...
                      </>
                    ) : (
                      "Simpan"
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>

      {warningAlert ? (
        <SweetAlert
          warning
          title="Warning"
          onConfirm={() => setWarningAlert(false)}
        >
          Pilih teacher terlebih dahulu!
        </SweetAlert>
      ) : null}

      {successAlert ? (
        <SweetAlert
          success
          title="Success!"
          confirmBtnText="Yes"
          confirmBtnBsStyle="primary"
          onConfirm={handleSuccess}
        >
          Success Add Teacher
        </SweetAlert>
      ) : null}

      {errorAlert ? (
        <SweetAlert
          error
          title="Oppss!"
          confirmBtnText="Yes"
          confirmBtnBsStyle="primary"
          onConfirm={() => setErrorAlert(false)}
        >
          {errorMessage}
        </SweetAlert>
      ) : null}
    </>
  )
}

AddTeacher.propTypes = {
  classId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  modalShow: PropTypes.bool,
  onSuccess: PropTypes.func,
  toggleModal: PropTypes.func,
}

export default AddTeacher
